import { useRef, useState, useEffect } from "react";
import { Button, BlockStack, Banner, Text, Card } from "@shopify/polaris";
import { useFormContext, Controller } from "react-hook-form";
import PropTypes from "prop-types";

const FILE_SIZE = 1024 * 1024; // 1MB
const FILE_SUPPORTED_FORMATS = ["image/png", "image/jpeg", "image/gif", "image/webp"];

RHFLogoUpload.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  helpText: PropTypes.string,
  rules: PropTypes.object,
};

export default function RHFLogoUpload({ name, label, helpText, rules }) {
  const { control, formState } = useFormContext();
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const previousValueRef = useRef(null);

  // Reset preview when form is reset or submission is cancelled
  useEffect(() => {
    if (!formState.isDirty && previousValueRef.current) {
      // Form was reset, restore original preview
      setPreview(previousValueRef.current.previewUrl || "");

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [formState.isDirty]);

  const handleImageChange = (event, onChange) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!FILE_SUPPORTED_FORMATS.includes(file.type)) {
      setError("Please upload a valid image file (JPEG, PNG, GIF, WebP).");
      return;
    }

    if (file.size > FILE_SIZE) {
      setError("File size should not exceed 1MB.");
      return;
    }

    setError("");

    // Store previous value for restoration on cancel
    if (!previousValueRef.current) {
      previousValueRef.current = {
        value: onChange.getMockImplementation ? onChange.getMockImplementation() : null,
        previewUrl: preview,
      };
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
      onChange(reader.result);

      // Trigger form change for save bar
      triggerFormChange();
    };
    reader.readAsDataURL(file);
  };

  const defaultLogo = "/images/no-image.png";

  const handleRemoveImage = (onChange, value) => {
    // Store current value before removing
    if (!previousValueRef.current) {
      previousValueRef.current = {
        value: value,
        previewUrl: preview,
      };
    }

    onChange(defaultLogo);
    setPreview("");
    setError("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Trigger form change for save bar
    triggerFormChange();
  };

  const triggerFormChange = () => {
    // Create a synthetic change event to trigger the save bar
    const event = new Event("change", { bubbles: true });
    document.getElementById(`${name}-hidden-input`)?.dispatchEvent(event);
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue=""
      render={({ field: { value, onChange }, fieldState: { error: fieldError } }) => {
        const logoSrc = preview || (typeof value === "string" && value.length > 0 ? value : defaultLogo);

        // Reset previous value reference when form is submitted successfully
        if (formState.isSubmitSuccessful) {
          previousValueRef.current = null;
        }

        return (
          <BlockStack gap="400">
            {label && (
              <Text variant="bodyMd" as="p" fontWeight="semibold">
                {label}
              </Text>
            )}
            {helpText && (
              <Text variant="bodySm" as="p" tone="subdued">
                {helpText}
              </Text>
            )}

            {(error || fieldError) && (
              <Banner status="critical" onDismiss={() => setError("")}>
                {error || fieldError?.message}
              </Banner>
            )}

            <div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageChange(e, onChange)}
                ref={fileInputRef}
                style={{ display: "none" }}
              />
              <input
                id={`${name}-hidden-input`}
                type="text"
                value={value || ""}
                onChange={() => {}}
                style={{ display: "none" }}
              />

              <Card>
                <div
                  style={{
                    width: "100%",
                    height: "200px",
                    backgroundColor: "#f6f6f7",
                    borderRadius: "8px",
                    border: "2px dashed #d1d5db",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {logoSrc && logoSrc !== defaultLogo ? (
                    <>
                      <img
                        src={logoSrc}
                        alt="Logo"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          display: "flex",
                          gap: "8px",
                        }}
                      >
                        <Button size="slim" onClick={() => fileInputRef.current?.click()}>
                          Change
                        </Button>
                        <Button size="slim" tone="critical" onClick={() => handleRemoveImage(onChange, value)}>
                          Remove
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div style={{ textAlign: "center" }}>
                      <Button onClick={() => fileInputRef.current?.click()}>Add image</Button>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </BlockStack>
        );
      }}
    />
  );
}
